export interface UserFriendlyError {
    title: string;
    message: string;
    icon?: string;
    variant?: 'danger' | 'warning' | 'info';
}

/**
 * Transforms any error into a user-friendly message object.
 * Hides raw system errors and provides actionable feedback.
 */
export function getFriendlyErrorMessage(error: unknown): UserFriendlyError {
    // Default fallback
    const defaultError: UserFriendlyError = {
        title: 'Unexpected Error',
        message: 'Something went wrong. Please try again later or contact support if the problem persists.',
        icon: 'ExclamationTriangleIcon',
        variant: 'danger'
    };

    if (!error) return defaultError;

    // Extract the message string
    const errorMessage = error instanceof Error
        ? error.message
        : typeof error === 'string'
            ? error
            : typeof error === 'object' && error !== null && 'message' in error
                ? (error as any).message
                : JSON.stringify(error);

    const lowerMsg = errorMessage.toLowerCase();

    // Network / Connection Issues
    if (
        lowerMsg.includes('fetch') ||
        lowerMsg.includes('network') ||
        lowerMsg.includes('connection') ||
        lowerMsg.includes('offline') ||
        lowerMsg.includes('failed to fetch')
    ) {
        return {
            title: 'Connection Lost',
            message: 'We couldn\'t connect to the server. Please check your internet connection and try again.',
            icon: 'WifiIcon',
            variant: 'warning'
        };
    }

    // Authentication Issues
    if (
        lowerMsg.includes('auth') ||
        lowerMsg.includes('session') ||
        lowerMsg.includes('login') ||
        lowerMsg.includes('jwt') ||
        lowerMsg.includes('token') ||
        lowerMsg.includes('unauthorized') ||
        lowerMsg.includes('forbidden')
    ) {
        return {
            title: 'Authentication Required',
            message: 'Your session has expired or is invalid. Please sign in again to continue.',
            icon: 'UserCircleIcon',
            variant: 'warning'
        };
    }

    // Database / Supabase Specifics
    if (lowerMsg.includes('duplicate key') || lowerMsg.includes('unique constraint')) {
        return {
            title: 'Already Exists',
            message: 'This record already exists. Please check your data to ensure it\'s unique.',
            icon: 'DocumentDuplicateIcon',
            variant: 'warning'
        };
    }

    if (lowerMsg.includes('violates row-level security policy')) {
        return {
            title: 'Permission Denied',
            message: 'You do not have permission to perform this action.',
            icon: 'ShieldExclamationIcon',
            variant: 'danger'
        };
    }

    if (lowerMsg.includes('foreign key constraint')) {
        return {
            title: 'Operation Failed',
            message: 'This action cannot be completed because it depends on other missing data.',
            icon: 'LinkIcon',
            variant: 'warning'
        };
    }

    // Validation / Input
    if (lowerMsg.includes('validation') || lowerMsg.includes('invalid') || lowerMsg.includes('required')) {
        return {
            title: 'Invalid Input',
            message: 'Please check your inputs and try again. Some fields may be missing or incorrect.',
            icon: 'ExclamationCircleIcon',
            variant: 'warning'
        };
    }

    // Timeouts
    if (lowerMsg.includes('timeout') || lowerMsg.includes('timed out')) {
        return {
            title: 'Request Timed Out',
            message: 'The server took too long to respond. Please try again.',
            icon: 'ClockIcon',
            variant: 'warning'
        };
    }

    // Rate Limiting
    if (lowerMsg.includes('rate limit') || lowerMsg.includes('too many requests')) {
        return {
            title: 'Slow Down',
            message: 'You are making too many requests. Please wait a moment before trying again.',
            icon: 'HandRaisedIcon',
            variant: 'warning'
        };
    }

    // 404 / Not Found
    if (lowerMsg.includes('not found') || lowerMsg.includes('404')) {
        return {
            title: 'Not Found',
            message: 'The requested resource could not be found.',
            icon: 'QuestionMarkCircleIcon',
            variant: 'info'
        };
    }

    // If we have a specific string message but didn't match categories, 
    // we might want to show it if it's not "system-y", but safe bet is default or generic wrapper.
    // Ideally, if it's a short message, we might show it? 
    // But user said "remove ALL system error messages". 
    // So we default to generic unless we can categorize it.

    // NOTE: For development, we might want to log the original error.
    console.warn('Unhandled System Error:', errorMessage);

    return defaultError;
}
